import { useTranslation } from "../../i18n";

/**
 * Notification log viewer for a campaign.
 *
 * `log` is the current page from the backend; `offset`/`onOffsetChange` own
 * the paging. The backend reports the real total, which the previous
 * implementation replaced with "rows in this page (+1 if another page exists)"
 * — so a campaign with 5 000 sends displayed "201".
 */
export default function CampaignLog({ log, offset = 0, onOffsetChange }) {
  const { t } = useTranslation();

  if (!log || !log.items || log.items.length === 0) {
    return (
      <div className="text-c-gray-400 text-sm py-4 text-center">
        {t("campaign.noNotifications")}
      </div>
    );
  }

  const total = log.total ?? 0;
  const limit = log.limit ?? 200;
  const from = total === 0 ? 0 : offset + 1;
  const to = offset + log.items.length;
  const canPage = typeof onOffsetChange === "function";
  const canGoBack = canPage && offset > 0;
  const canGoForward = canPage && log.hasNext && to < total;

  return (
    <div>
      <div className="overflow-auto max-h-[300px]">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-c-gray-400/30">
              <th className="text-left py-2 px-2 text-c-gray-400">
                {t("campaign.colCustomer")}
              </th>
              <th className="text-left py-2 px-2 text-c-gray-400">
                {t("campaign.colPhone")}
              </th>
              <th className="text-left py-2 px-2 text-c-gray-400">
                {t("campaign.colSentAt")}
              </th>
              <th className="text-left py-2 px-2 text-c-gray-400">
                {t("campaign.colVariant")}
              </th>
            </tr>
          </thead>
          <tbody>
            {log.items.map((entry) => (
              <tr key={entry.id} className="border-b border-c-gray-400/10">
                <td className="py-2 px-2">
                  {entry.firstname || `#${entry.customerId}`}
                </td>
                <td className="py-2 px-2 text-c-gray-400">
                  {entry.phone || "—"}
                </td>
                <td className="py-2 px-2 text-c-gray-400">
                  {new Date(entry.sentAt).toLocaleString()}
                </td>
                <td className="py-2 px-2">{entry.variant || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between pt-2 text-c-gray-400 text-xs">
        <span>{t("campaign.logRange", { from, to, total })}</span>
        {canPage && (
          <span className="flex gap-1">
            <button
              type="button"
              className="px-2 py-1 rounded bg-c-gray-500 hover:bg-c-gray-400 disabled:opacity-40"
              onClick={() => onOffsetChange(Math.max(0, offset - limit))}
              disabled={!canGoBack}
            >
              {t("campaign.logPrev")}
            </button>
            <button
              type="button"
              className="px-2 py-1 rounded bg-c-gray-500 hover:bg-c-gray-400 disabled:opacity-40"
              onClick={() => onOffsetChange(offset + limit)}
              disabled={!canGoForward}
            >
              {t("campaign.logNext")}
            </button>
          </span>
        )}
      </div>
    </div>
  );
}
